import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { isWhitelabel } from "../utils/Constants";

// Defaults from public/index.html, restored when a whitelabel user logs out.
const DEFAULT_ICON = "https://cdn.mywanderlinx.com/assets/favicon/favicon.ico";
const DEFAULT_TOUCH_ICON = "https://cdn.mywanderlinx.com/assets/favicon/logo192.png";

// A transparent 1x1 GIF — a "no icon" that still stops the browser falling
// back to /favicon.ico (which is the Wanderlinx one).
const BLANK_ICON =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// Pages that must not show Wanderlinx branding before anyone signs in —
// /login is the sign-in page whitelabel tenants' domains use (getSignInUrl),
// /quotation/:token is the public Client Inquiry Link page clients open
// without ever signing in.
const UNBRANDED_PATHS = ["/login"];
const UNBRANDED_PREFIXES = ["/quotation/"];

const setLinkHref = (rel: string, href: string) => {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
};

/**
 * Swaps the browser-tab icon for every page a whitelabel admin or its agents
 * view: their admin's /branding-setup favicon (UserResource
 * whitelabel_branding.favicon), falling back to the logo when no favicon has
 * been uploaded. /login and /quotation/:token show no icon at all (no brand
 * to show pre-sign-in, and quotation links go out to clients of any agency).
 * Everyone else keeps the Wanderlinx favicon. Mounted once in App, inside
 * the Router.
 */
const WhitelabelFavicon: React.FC = () => {
  const currentUser = useSelector((state: any) => state.currentUser);
  const branding = currentUser?.whitelabel_branding;
  const brandIcon = isWhitelabel(currentUser)
    ? branding?.favicon || branding?.logo || null
    : null;

  const { pathname } = useLocation();
  const isUnbranded =
    UNBRANDED_PATHS.includes(pathname.replace(/\/+$/, "")) ||
    UNBRANDED_PREFIXES.some((prefix) => pathname.startsWith(prefix));

  useEffect(() => {
    const fallback = isUnbranded ? BLANK_ICON : null;
    setLinkHref("icon", brandIcon || fallback || DEFAULT_ICON);
    setLinkHref("apple-touch-icon", brandIcon || fallback || DEFAULT_TOUCH_ICON);
  }, [brandIcon, isUnbranded]);

  return null;
};

export default WhitelabelFavicon;
